import { useState } from 'react';
import { type Criterion } from '../types';
import { Plus, Trash2, ArrowRight, Sparkles } from 'lucide-react';
import { getRICECriteria } from '../utils/scoring';

interface CriteriaStepProps {
  criteria: Criterion[];
  onNext: (criteria: Criterion[]) => void;
}

export function CriteriaStep({ criteria, onNext }: CriteriaStepProps) {
  const [localCriteria, setLocalCriteria] = useState<Criterion[]>(
    criteria.length > 0
      ? criteria
      : [
          {
            id: 'criterion-0',
            name: '',
            description: '',
            weight: 0,
          },
        ]
  );

  const handleAddCriterion = () => {
    setLocalCriteria([
      ...localCriteria,
      {
        id: `criterion-${Date.now()}`,
        name: '',
        description: '',
        weight: 0,
      },
    ]);
  };

  const handleRemoveCriterion = (id: string) => {
    if (localCriteria.length > 1) {
      setLocalCriteria(localCriteria.filter((c) => c.id !== id));
    }
  };

  const handleCriterionChange = (id: string, field: 'name' | 'description', value: string) => {
    setLocalCriteria(
      localCriteria.map((c) => (c.id === id ? { ...c, [field]: value } : c))
    );
  };

  const handleUseRICE = () => {
    const riceCriteria = getRICECriteria().map((c, index) => ({
      id: `criterion-${Date.now()}-${index}`,
      name: c.name,
      description: c.description,
      weight: 0,
    }));
    setLocalCriteria(riceCriteria);
  };

  const handleNext = () => {
    const validCriteria = localCriteria.filter(
      (c) => c.name.trim().length > 0
    );

    if (validCriteria.length < 1) {
      alert('Please add at least one criterion.');
      return;
    }

    onNext(validCriteria);
  };

  const validCount = localCriteria.filter((c) => c.name.trim().length > 0).length;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-2">
              Define Evaluation Criteria
            </h2>
            <p className="text-sm text-[var(--text-sec)]">
              What dimensions will you use to evaluate each item?
            </p>
          </div>
          <button
            onClick={handleUseRICE}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--slate-dim)] text-[var(--text-sec)] hover:bg-[var(--slate-dim)] transition-colors"
          >
            <Sparkles className="h-4 w-4" />
            Use RICE
          </button>
        </div>

        <div className="space-y-4 mb-4">
          {localCriteria.map((criterion, index) => (
            <div key={criterion.id} className="p-4 border border-[var(--rule)]">
              <div className="flex items-start justify-between mb-2">
                <span className="text-sm font-medium text-[var(--text-ter)]">
                  Criterion {index + 1}
                </span>
                {localCriteria.length > 1 && (
                  <button
                    onClick={() => handleRemoveCriterion(criterion.id)}
                    className="text-[var(--coral)] hover:text-[var(--coral)]"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="space-y-2">
                <input
                  type="text"
                  value={criterion.name}
                  onChange={(e) => handleCriterionChange(criterion.id, 'name', e.target.value)}
                  placeholder="e.g., Impact, Effort, Cost, Risk"
                  className="w-full px-3 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <textarea
                  value={criterion.description}
                  onChange={(e) => handleCriterionChange(criterion.id, 'description', e.target.value)}
                  placeholder="How would you describe 'high' vs 'low' for this criterion?"
                  rows={2}
                  className="w-full px-3 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={handleAddCriterion}
          className="mb-4 flex items-center gap-2 text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] px-3 py-1 rounded hover:bg-[var(--hover-tint)] transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add Criterion
        </button>

        <div className="mt-6 pt-4 border-t border-[var(--rule)]">
          <div className="flex items-center justify-between">
            <p className="text-sm text-[var(--text-sec)]">
              {validCount} criteri{validCount === 1 ? 'on' : 'a'} defined
            </p>
            <button
              onClick={handleNext}
              disabled={validCount < 1}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:bg-[var(--surface)] disabled:cursor-not-allowed transition-colors"
            >
              Continue
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

